<template>
  <div v-if="artistLoaded">
    <div class="header blur">
      <div
        v-if="artist.image_profile"
        :style="{ backgroundImage: 'url(' + artist.image_profile + ')' }"
        class="bg-image"
      />
      <div
        v-if="!artist.image_profile"
        class="bg-image"
      />
    </div>
    <div class="container">
      <div class="gutter">
        <div class="columns">
          <div class="column is-narrow sidebar is-hidden-touch">
            <section class="artist-profile">
              <div class="cover-large">
                <figure class="image is-square">
                  <img
                    v-if="artist.image_profile"
                    :src="artist.image_profile"
                  >
                  <img
                    v-if="!artist.image_profile"
                    src="@/assets/artist-default.png"
                  >
                </figure>
              </div>
            </section>
            <section v-if="artist.biography">
              <h4 class="title is-4">
                Bio
              </h4>
              <p class="margin-bottom">
                {{ artist.biography }}
              </p>
            </section>

            <section v-if="relatedArtists.length > 0">
              <h4 class="title is-4">
                Similar artists
              </h4>
              <div
                v-for="relatedArtist in relatedArtists"
                :key="relatedArtist.pk"
                class="artist-small margin-bottom"
              >
                <router-link
                  :to="{ name: 'artist-detail', params: { slug: relatedArtist.slug, artistId: relatedArtist.pk }}"
                >
                  <div class="columns is-vcentered is-0">
                    <div class="column is-narrow">
                      <figure class="image is-rounded is-48x48">
                        <img
                          v-if="relatedArtist.image_avatar"
                          class="is-rounded"
                          :src="relatedArtist.image_avatar"
                        >
                        <img
                          v-if="!relatedArtist.image_avatar"
                          class="is-rounded"
                          src="@/assets/artist-default.png"
                        >
                      </figure>
                    </div>
                    <div class="column">
                      <span class="text label-medium">{{ relatedArtist.name }}</span>
                    </div>
                  </div>
                </router-link>
              </div>
            </section>
          </div>
          <div
            v-if="artist"
            class="column main"
          >
            <h2 class="subtitle is-marginless text color-red">
              Artist
            </h2>
            <h1 class="title is-1 text color-white">
              {{ artist.name }}
            </h1>
            <div
              class="box is-large"
            >
              <template v-if="releasesLoaded">
                <h3 class="title is-3">
                  Releases
                </h3>
                <release-list-item
                  v-for="release in sortedReleases"
                  :key="release.pk"
                  :release="release"
                />
              </template>
              <template v-else>
                <div class="columns">
                  <div class="column is-12 has-text-centered">
                    <br>
                    <span
                      class="loader xl"
                      style="display: inline-block; width: 3em; height: 3em;"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import artistClient from '@/api/artists.js'
import releaseClient from '@/api/releases.js'

import ReleaseListItem from '@/components/ReleaseListItem.vue'

export default {
  name: 'ArtistDetail',

  components: {
    ReleaseListItem
  },

  props: {
    artistId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      artist: null,
      relatedArtists: [],
      releases: [],
      artistLoaded: false,
      releasesLoaded: false
    }
  },

  computed: {
    // The play queue, in the order the page shows the tracks: releases by title, then
    // each release's tracks by track number (as ReleaseListItem orders them).
    noInstrumentalVersionTracks: function () {
      const tracks = []
      this.sortedReleases.forEach(release => {
        tracks.push(...release.tracks
          .filter((track) => track.is_instrumental_version === false)
          .sort((a, b) => a.track_number > b.track_number ? 1 : b.track_number > a.track_number ? -1 : 0)
        )
      })
      return tracks
    },

    sortedReleases: function () {
      const releases = [...this.releases]
      // localeCompare returns -1/0/1; the old comparator returned a boolean, which isn't a
      // valid sort result, so the order depended on how the releases happened to arrive.
      return releases.sort((a, b) => a.display_title.localeCompare(b.display_title, undefined, { sensitivity: 'base' }))
    }
  },

  created () {
    const vm = this
    // Grab artist from API, then release data.
    artistClient.retrieve(vm.artistId)
      .then(response => {
        vm.artist = response.data

        if (vm.artist.status !== 'visible') {
          this.$router.push({ name: 'not-found' })
        }

        vm.artistLoaded = true

        // Let's build an array of calls to get release data so we can track when they
        // are ALL finished and then load the tracks.
        const releaseCallPromises = []

        vm.artist.releases.forEach(function (releaseId) {
          releaseCallPromises.push(
            releaseClient.retrieve(releaseId).then(response => {
              const release = response.data
              const coverKeys = Object.keys(release).filter(key => key.startsWith('cover_'))

              if (release.tracks.length > 0) {
                release.tracks.forEach(function (track) {
                  coverKeys.forEach(function (key) {
                    track[key] = release[key]
                  })
                })
                vm.releases.push(release)
              }
            })
          )
        })

        // Fire off release lookups then add tracks to player playlist when finished.
        Promise.all(releaseCallPromises).then(function () {
          vm.releasesLoaded = true
          if (vm.noInstrumentalVersionTracks.length > 0) {
            vm.$store.dispatch('player/loadTracks', vm.noInstrumentalVersionTracks)
          }
        })

        vm.artist.related_artists.forEach(function (artistId) {
          artistClient.retrieve(artistId)
            .then(response => {
              vm.relatedArtists.push(response.data)
            })
            .catch(() => {})
        })
      })
      .catch(error => {
        if (error.response.status === 404) {
          this.$router.push({ name: 'not-found' })
        }
      })
  },

  metaInfo () {
    if (this.artist) {
      let url = window.location.origin
      const path = this.$route.path
      if (path !== '/') {
        url += path
      }
      const title = `${this.artist.name} | Music Licensing`
      const description = this.artist.biography || ''

      return {
        title,
        meta: [
          { name: 'description', content: description },
          { property: 'og:url', content: url },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { property: 'og:image', content: this.artist.image_profile || '/img/sonata-meta.png' },
          { name: 'twitter:card', content: 'website' }
        ]
      }
    } else {
      return {
        title: 'Music Licensing'
      }
    }
  }
}
</script>

<style lang="scss">
.column.main {
  margin-top: 64px;
}

.column.is-narrow.sidebar {
  width: 300px;
  margin-right: 24px;
  margin-top: 32px;
  section {
    margin-bottom: 32px;
    .artist-profile {
      min-height: 288px;
      img {
        width: 288px;
        height: 288px;
      }
    }
  }
}
</style>
